import { styled } from "styled-components";
import { COLORS } from "../../constants/colors/colors";
import { BackArrowIcon, CloseIcon } from "../../constants/icons/shapeIcons";
import { GlassmorphismContainer } from "../GlassmorphismContainer/GlassmorphismContainer";
import { getContrastTextColor } from "../../utils/glassmorphismUtils";
import { useAppSelector } from "../../store/hooks";

export type NavButtonVariant = "back" | "exit";

export const NavButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 1rem;
`;

export const GenericNavButton = styled.button<{ variant: NavButtonVariant }>`
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  min-height: 3rem;
  border-radius: 50%;
  border: none;
  background: ${COLORS.GRAY};
  color: ${COLORS.IMMERSIVE_BLUE_DARK};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  font-size: ${(p) => (p.variant === "exit" ? "1.5rem" : "inherit")};
  font-weight: ${(p) => (p.variant === "exit" ? 700 : "inherit")};
  padding: 0;
  aspect-ratio: 1;
  flex-shrink: 0;

  &:hover {
    background-color: ${COLORS.SILVER};
  }
`;

export const GlassmorphismNavButton = styled(GenericNavButton)`
  background: transparent;
  color: ${COLORS.IMMERSIVE_BLUE_DARK};

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }
`;

export const PrimaryNavButton = styled(GenericNavButton)<{
  width?: string;
  height?: string;
}>`
  background-color: ${COLORS.BOUNDLESS_BLUE};
  color: #fff;
  width: ${(p) => p.width || "3rem"};
  height: ${(p) => p.height || "3rem"};
  min-width: ${(p) => p.width || "3rem"};
  min-height: ${(p) => p.height || "3rem"};

  &:hover {
    background-color: ${COLORS.BOUNDLESS_BLUE};
    opacity: 0.9;
    color: ${COLORS.WHITE};
  }
`;

export const SecondaryNavButton = styled(GenericNavButton)<{
  width?: string;
  height?: string;
}>`
  background-color: ${COLORS.GRAY};
  color: ${COLORS.IMMERSIVE_BLUE_DARK};
  width: ${(p) => p.width || "3rem"};
  height: ${(p) => p.height || "3rem"};
  min-width: ${(p) => p.width || "3rem"};
  min-height: ${(p) => p.height || "3rem"};

  &:hover {
    background-color: ${COLORS.SILVER};
    opacity: 0.9;
    color: ${COLORS.IMMERSIVE_BLUE_DARK};
  }
`;

type NavButtonStyle = "primary" | "secondary";

interface BaseNavButtonProps {
  onClick: () => void;
  button: NavButtonVariant;
  withContainer?: boolean;
  ariaLabel?: string;
  size?: string;
}

export const NavButton = ({
  onClick,
  button,
  withContainer,
  ariaLabel,
  variant = "secondary",
  size,
}: BaseNavButtonProps & { variant?: NavButtonStyle }) => {
  const content =
    button === "back" ? (
      <BackArrowIcon
        width="1.25rem"
        height="1.25rem"
        fill={variant === "primary" ? COLORS.WHITE : COLORS.IMMERSIVE_BLUE_DARK}
      />
    ) : (
      <CloseIcon
        width="0.875rem"
        height="0.875rem"
        fill={variant === "primary" ? COLORS.WHITE : COLORS.IMMERSIVE_BLUE_DARK}
      />
    );

  const defaultAriaLabel = button === "back" ? "Back" : "Exit";

  const element =
    variant === "primary" ? (
      <PrimaryNavButton
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? defaultAriaLabel}
        variant={button}
        width={size}
        height={size}
      >
        {content}
      </PrimaryNavButton>
    ) : (
      <SecondaryNavButton
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? defaultAriaLabel}
        variant={button}
        width={size}
        height={size}
      >
        {content}
      </SecondaryNavButton>
    );

  if (withContainer) return <NavButtonContainer>{element}</NavButtonContainer>;
  return element;
};

interface GlassmorphismNavButtonProps {
  onClick: () => void;
  button: NavButtonVariant;
  withContainer?: boolean;
  ariaLabel?: string;
}

export const NavButtonGlassmorphism = ({
  onClick,
  button,
  withContainer,
  ariaLabel,
}: GlassmorphismNavButtonProps) => {
  const contrast = useAppSelector((state) => state.userInfo.contrast);

  const content =
    button === "back" ? (
      <BackArrowIcon
        width="1.25rem"
        height="1.25rem"
        fill={getContrastTextColor(contrast)}
      />
    ) : (
      <CloseIcon
        width="0.875rem"
        height="0.875rem"
        fill={getContrastTextColor(contrast)}
      />
    );

  const defaultAriaLabel = button === "back" ? "Back" : "Exit";

  const element = (
    <GlassmorphismContainer
      width="3rem"
      height="3rem"
      borderRadius="50%"
      alignItems="center"
      justifyContent="center"
      hasBorder={true}
      contrast={contrast}
    >
      <GlassmorphismNavButton
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? defaultAriaLabel}
        variant={button}
      >
        {content}
      </GlassmorphismNavButton>
    </GlassmorphismContainer>
  );

  if (withContainer) return <NavButtonContainer>{element}</NavButtonContainer>;
  return element;
};
